import { create } from "zustand";
import { useTemporaryJobPostingStore } from "./temporaryJobPostingStore";
import { useJobPostingStepperStore } from "./jobPostingStepperStore";
import { useDraftJobStore, type DraftItem } from "./draftJobStore";
import { queryClient } from "../pages/_app";
import { categoriesKeys, Category } from "queries/categories";
import { JobPostFieldsData, jobPostFieldsKeys } from "queries/jobPostFields";
import Router from "next/router";
import { Routes } from "routes/RouteConstants";
import useModalStore from "./useModalStore";
import { modalStateValues, modalType } from "services/constants";
import { ApiJobTitlesOptions, jobTitlesKeys } from "queries/jobTitles";
import { mapJobTitlesOptions } from "queries/jobTitles/useJobTitlesQuery";
import { JOBPOSTING_V2_EVENTS } from "utils/jobPostingv2Events";
import LogService from "services/LogService";

export interface FieldError {
  isError: boolean;
  errorMsg?: string;
  showError?: boolean;
  customError?: {
    isSalaryError?: boolean;
    isIncentivesError?: boolean;
  };
}

export interface FieldState {
  id: string;
  visible: boolean;
  sectionId?: string;
  is_mandatory?: boolean;
  touched?: boolean;
  dirty?: boolean;
  value?: any;
  options?: any[];
  error?: FieldError;
  labelType?: string;
  defaultOption?: any;
  medianValue?: any;
}

interface JobPostingState {
  draftId?: string;
  jobId?: string;
  jobPlan?: string;
  isDraft?: boolean;
  localState: FieldState[];
  finalState: FieldState[];
  listofjobpostfields: string[]; // List of all available job post field names
  fieldConfigMapping: Record<string, FieldConfig>; // Unified field configuration with sectionId and apiKey
  draftJobApiKeyMapping: DraftJobApiKeyMapping; // Mapping for draft job API keys
  isCategorySetManually: boolean; // Indicates if category_id was set manually via JobTitle or JobCategory components
  lastSavedJobTitle: string;
  setLastSavedJobTitle: (jobTitle: string) => void;
  computeStore: () => void;
  getFld: (id: string, state?: "local" | "final") => FieldState | undefined;
  setValue: (
    id: string,
    value: any,
    state?: "local" | "final",
    enableCategoryChangeAlert?: boolean
  ) => Promise<boolean>;
  setFieldProperty: (
    id: string,
    property: keyof FieldState,
    value: any,
    state?: "local" | "final"
  ) => void;
  validateAndProceed: (activeTabId: string) => boolean;
  updateFieldStateWithMeta: (
    fieldStates: FieldState[],
    state?: "local" | "final"
  ) => void;
  updateFieldStateFromJobData: (
    fieldStates: FieldState[],
    state?: "local" | "final"
  ) => void;
  hydrateFieldValuesFromJobData: (
    jobData: any,
    currentFieldStates: FieldState[]
  ) => FieldState[];
  hydrateFromTemporaryStore: () => void;
  hydrateFromRemoteDraft: (draftItem: DraftItem) => Promise<void>;
  setDraftId: (draftId: string) => void;
  setJobId: (jobId: string) => void;
  setJobPlan: (jobPlan: string) => void;
  clearStore: () => void;
  setCategorySetManually: (isManual: boolean) => void;
  updateFieldsOnCategoryChange: () => void;
  clearLocalStore: () => void;
  setIsDraft: (isDraft: boolean) => void;
  shouldAlertUserOnCategoryChange: ({
    id,
    value
  }: {
    id: string;
    value: any;
  }) => boolean;
  resetAllFieldErrors: (state?: "local" | "final") => void;
}
const loadLocalState = () => {
  return [];
};

// Unified field configuration mapping: fieldId -> { sectionId, apiKey }
export interface FieldConfig {
  sectionId: string;
  apiKey?: string; // Optional because some fields don't have API mapping
}

export const CUSTOM_CHIP_ID = 999999999;
// Draft job API key mapping: fieldId -> draft job API key
// This maps local state field IDs to the API keys used in draft job data (from getAllAvailableFields)
export interface DraftJobApiKeyMapping {
  [fieldId: string]: string | string[]; // Can be single key or multiple keys
}
const fieldConfigMapping: Record<string, FieldConfig> = {
  // Basic Info Section
  job_title: { sectionId: "basic-info", apiKey: "title" },
  job_city_id: { sectionId: "basic-info", apiKey: "city" },
  job_locality_id: { sectionId: "basic-info", apiKey: "locality" },
  category_id: { sectionId: "basic-info", apiKey: "categories" },
  work_location: { sectionId: "basic-info", apiKey: "job_type_location" },
  distance_preference: {
    sectionId: "basic-info",
    apiKey: "distance_preference"
  },
  job_type: { sectionId: "basic-info", apiKey: "job_type" },

  // Job Details Section (matches 'job-details' step in jobPostingStepperStore)
  client_name: { sectionId: "job-details" }, // No API mapping
  vacancy: { sectionId: "basic-info", apiKey: "no_of_openings" },
  communication_preference: { sectionId: "basic-info" }, // No API mapping
  interview_city_id: { sectionId: "job-details", apiKey: "interview_city_id" },
  interview_locality_id: {
    sectionId: "job-details",
    apiKey: "interview_locality_id"
  },
  interview_type: { sectionId: "job-details", apiKey: "interview_type_label" },
  is_address_same_as_company: {
    sectionId: "job-details",
    apiKey: "is_address_same_as_company"
  },
  interview_address: { sectionId: "job-details", apiKey: "interview_address" },
  salary: { sectionId: "job-details" }, // Special handling - uses minimum_salary & maximum_salary
  is_target_based: { sectionId: "job-details", apiKey: "is_target_based" },
  is_per_packet: { sectionId: "job-details", apiKey: "is_per_packet_based" },
  jobTimings: { sectionId: "job-details", apiKey: "job_timings" },
  depositRequired: {
    sectionId: "job-details",
    apiKey: "is_additional_fee_applicable"
  },
  depositAmount: { sectionId: "job-details", apiKey: "amount" },
  depositReason: {
    sectionId: "job-details",
    apiKey: "job_fee_deposit_reasons"
  },
  depositTime: { sectionId: "job-details", apiKey: "job_fee_deposit_time" },
  shiftType: { sectionId: "job-details", apiKey: "shift_type" },
  is_contractual: { sectionId: "job-details", apiKey: "is_contract" },
  famous_location: { sectionId: "job-details" }, // No API mapping
  workingDays: { sectionId: "job-details", apiKey: "working_days" },
  jobBenefits: { sectionId: "job-details", apiKey: "company_perks" },

  // Job Description Section (matches 'job-description' step in jobPostingStepperStore)
  job_description: {
    sectionId: "job-description",
    apiKey: "enhanced_job_description"
  },

  // Candidate Details Section (matches 'candidate-details' step in jobPostingStepperStore)
  gender: { sectionId: "candidate-details", apiKey: "gender" },
  qualification: {
    sectionId: "candidate-details",
    apiKey: "minimum_qualification"
  },
  experience: { sectionId: "candidate-details", apiKey: "experience" },
  asset: { sectionId: "candidate-details", apiKey: "assets_v2" },
  englishFluency: {
    sectionId: "candidate-details",
    apiKey: "english_fluency_job_posting_form"
  },
  regionalLanguages: {
    sectionId: "candidate-details",
    apiKey: "regional_languages"
  },
  skills: { sectionId: "candidate-details", apiKey: "skills" },
  industry: { sectionId: "candidate-details", apiKey: "prior_experience_type" },
  industryExp: { sectionId: "candidate-details" }
};

// Draft job API key mapping (based on getAllAvailableFields function)
const draftJobApiKeyMapping: DraftJobApiKeyMapping = {
  // Basic fields
  vacancy: "no_of_openings",
  job_title: "job_title",
  client_name: "client_name",
  job_city_id: "job_city_id",
  interview_city_id: "interview_city_id",
  job_locality_id: "job_locality_id",
  interview_locality_id: "interview_locality_id",
  interview_type: "interview_type_label",
  is_address_same_as_company: "is_address_same_as_company",
  interview_address: "interview_address", // Note: can also be "client_address" for consultants
  category_id: "category_id",

  // Experience uses multiple keys
  experience: ["minExp", "maxExp", "is_fresher_allowed"],

  // Salary uses multiple keys
  salary: ["minSal", "maxSal", "incentives"],

  // Target and packet based
  is_target_based: "is_target_based",
  is_per_packet: "is_per_packet",

  // Work location
  work_location: "job_type_location",

  // Distance preference
  distance_preference: "distance_preferences",

  // Job description
  job_description: "enhanced_job_description",

  // Job timings
  jobTimings: ["job_end_time", "job_start_time"],

  // Gender
  gender: "gender_req",

  // Qualification
  qualification: "minimum_qualification",

  // Deposit fields
  depositRequired: "is_additional_fee_applicable",
  depositAmount: "amount",
  depositReason: "deposit_reason_id",
  depositTime: "deposit_time_id",

  // Shift type
  shiftType: "shift_type",

  // Assets
  asset: "assets_v2",

  // English fluency
  englishFluency: "english_fluency",

  // Regional languages
  regionalLanguages: "regional_languages",

  // Job type
  job_type: "job_type",

  // Contractual
  is_contractual: "is_contract",

  // Famous location
  famous_location: ["famous_location_category_id", "famous_location_id"],

  // Working days
  workingDays: "working_days",

  // Skills
  skills: "job_skills",

  // Industry
  industry: "prior_experience_type",

  // Job benefits
  jobBenefits: "job_perks",

  // Communication preference
  communication_preference: ["call_allowed", "job_contact"],
  industryExp: "is_industry_exp"
};

// Extract field names from the unified configuration
const listOfJobPostFields: string[] = Object.keys(fieldConfigMapping);

export const useJobPostingStore = create<JobPostingState>((set, get) => ({
  draftId: undefined,
  jobId: undefined,
  jobPlan: undefined,
  isDraft: false,
  localState: loadLocalState(),
  finalState: [],
  listofjobpostfields: listOfJobPostFields,
  fieldConfigMapping: fieldConfigMapping,
  draftJobApiKeyMapping: draftJobApiKeyMapping,
  isCategorySetManually: false,
  lastSavedJobTitle: "",

  computeStore: () => {
    // Copy localState to finalState for computation
    const { localState } = get();
    set({ finalState: [...localState] });
  },

  setLastSavedJobTitle: (jobTitle: string) => {
    set({ lastSavedJobTitle: jobTitle });
  },

  getFld: (id: string, state: "local" | "final" = "local") => {
    const { localState, finalState } = get();
    const targetState = state === "local" ? localState : finalState;
    return targetState.find(field => field.id === id);
  },

  setValue: async (
    id: string,
    value: any,
    state: "local" | "final" = "local",
    enableCategoryChangeAlert: boolean = true
  ) => {
    const {
      localState,
      finalState,
      isCategorySetManually,
      shouldAlertUserOnCategoryChange
    } = get();

    if (state === "local") {
      // Find the current field to get its sectionId
      const currentField = localState.find(field => field.id === id);
      if (
        (currentField?.id === "category_id" ||
          currentField?.id === "job_title") &&
        isCategorySetManually &&
        enableCategoryChangeAlert
      ) {
        const shouldAlert = shouldAlertUserOnCategoryChange({ id, value });
        if (shouldAlert) {
          const decisionPromise = new Promise<void>((resolve, reject) => {
            useModalStore.getState().setModalData({
              state: modalStateValues.open,
              type: modalType.CATEGORY_CHANGE_ALERT,
              onConfirm: () => {
                resolve();
              },
              onCancel: () => {
                reject();
              }
            });
          });
          LogService.log(JOBPOSTING_V2_EVENTS.CATEGORY_CHANGE_ALERT_OPENED);
          try {
            await decisionPromise;
          } catch (error) {
            return false;
          }
        }
      }
      const updatedLocalState = localState.map(field =>
        field.id === id
          ? { ...field, value, dirty: true, touched: true }
          : field
      );

      // If field doesn't exist, create it
      if (!localState.find(field => field.id === id)) {
        updatedLocalState.push({
          id,
          visible: true,
          value,
          dirty: true,
          touched: true
        });
      }

      set({ localState: updatedLocalState });

      // Mark the section as incomplete when field value changes
      // This ensures that if user modifies a field after completing a step,
      // they need to validate and save again to mark it complete
      if (currentField?.sectionId) {
        const stepperStore = useJobPostingStepperStore.getState();
        stepperStore.updateStepState(currentField.sectionId, "INCOMPLETE");
      }

      // Also update the temporary store
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.setValue(id, value, state);
    } else {
      // Find the current field to get its sectionId
      const currentField = finalState.find(field => field.id === id);

      const updatedFinalState = finalState.map(field =>
        field.id === id
          ? { ...field, value, dirty: true, touched: true }
          : field
      );

      // If field doesn't exist, create it
      if (!finalState.find(field => field.id === id)) {
        updatedFinalState.push({
          id,
          visible: true,
          value,
          dirty: true,
          touched: true
        });
      }

      set({ finalState: updatedFinalState });

      // Mark the section as incomplete when field value changes
      // This ensures that if user modifies a field after completing a step,
      // they need to validate and save again to mark it complete
      if (currentField?.sectionId) {
        const stepperStore = useJobPostingStepperStore.getState();
        stepperStore.updateStepState(currentField.sectionId, "INCOMPLETE");
      }

      // Also update the temporary store
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.setValue(id, value, state);
    }
    return true;
  },

  setFieldProperty: (
    id: string,
    property: keyof FieldState,
    value: any,
    state: "local" | "final" = "local"
  ) => {
    const { localState, finalState } = get();

    if (state === "local") {
      const updatedLocalState = localState.map(field =>
        field.id === id ? { ...field, [property]: value } : field
      );
      set({ localState: updatedLocalState });

      // Also update the temporary store
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.setFieldProperty(id, property, value, state);
    } else {
      const updatedFinalState = finalState.map(field =>
        field.id === id ? { ...field, [property]: value } : field
      );
      set({ finalState: updatedFinalState });

      // Also update the temporary store
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.setFieldProperty(id, property, value, state);
    }
  },

  validateAndProceed: (activeTabId: string) => {
    const { localState } = get();

    // Filter fields by the current section (activeTabId) and only visible fields
    const currentSectionFields = localState.filter(
      field => field.sectionId === activeTabId && field.visible === true
    );

    // Find all fields with errors in the current section that are visible
    const fieldsWithErrors = currentSectionFields.filter(
      field => field.error?.isError === true
    );

    if (fieldsWithErrors.length > 0) {
      // Set showError to true for all invalid fields that are visible
      const updatedLocalState = localState.map(field => {
        if (
          field.sectionId === activeTabId &&
          field.visible === true &&
          field.error?.isError === true
        ) {
          return {
            ...field,
            error: {
              ...field.error,
              showError: true
            }
          };
        }
        return field;
      });

      set({ localState: updatedLocalState });

      // Scroll the first invalid field into view
      if (fieldsWithErrors.length > 0) {
        const firstInvalidFieldId = fieldsWithErrors[0].id;
        const element = document.getElementById(`field-${firstInvalidFieldId}`);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }

      return false; // Validation failed, prevent navigation
    }

    return true; // Validation passed, allow navigation
  },
  hydrateFieldValuesFromJobData: (
    jobData: any,
    currentFieldStates: FieldState[]
  ): FieldState[] => {
    // Create a copy of current field states to avoid mutation
    const updatedFieldStates = currentFieldStates.map(field => ({ ...field }));

    // Create a map for efficient lookup of field states by id
    const fieldStatesMap = new Map(
      updatedFieldStates.map(field => [field.id, field])
    );

    const metaJobPostField: JobPostFieldsData = queryClient.getQueryData(
      jobPostFieldsKeys.byCategory(jobData.category_id)
    );
    // Use draftJobApiKeyMapping to map local state fields to draft job API keys
    Object.entries(draftJobApiKeyMapping).forEach(
      ([storeFieldKey, draftApiKeys]) => {
        const fieldState = fieldStatesMap.get(storeFieldKey);

        // Skip if field doesn't exist in current state
        if (!fieldState) return;

        // Get the corresponding meta API key from fieldConfigMapping
        const metaApiKey = fieldConfigMapping[storeFieldKey]?.apiKey;

        // Helper function to get job data value using draft API keys
        const getJobDataValue = (keys: string | string[]) => {
          if (typeof keys === "string") {
            return jobData[keys];
          }
          // For array of keys, return an object with all values
          const result: any = {};
          keys.forEach(key => {
            if (jobData[key] !== undefined) {
              result[key] = jobData[key];
            }
          });
          return Object.keys(result).length > 0 ? result : undefined;
        };

        // Helper function to get meta field options
        const getMetaFieldOptions = (apiKey: string): any[] => {
          return metaJobPostField?.[apiKey]?.options || [];
        };

        // Get the job data value using draft API keys
        const jobDataValue = getJobDataValue(draftApiKeys);
        if (jobDataValue === undefined) return;
        // Handle special field mappings that require custom logic
        const userProfile = queryClient.getQueryData(["userProfile"]);
        switch (storeFieldKey) {
          case "category_id":
            const categories = queryClient.getQueryData(categoriesKeys.list());
            fieldState.value = {
              id: jobDataValue,
              label:
                categories?.find(
                  (category: Category) => category.id === jobDataValue
                )?.title || ""
            };
            break;

          case "job_city_id":
          case "interview_city_id":
            const cities = queryClient.getQueryData(["cities"]);
            fieldState.value = {
              id: jobDataValue,
              label: cities?.find(city => city.id === jobDataValue)?.name || ""
            };
            break;

          case "job_locality_id":
          case "interview_locality_id":
            fieldState.value = { id: jobDataValue, label: "" };
            break;

          case "gender":
          case "qualification":
          case "job_type":
            fieldState.value = {
              id: jobDataValue,
              label: jobDataValue
            };
            break;

          case "experience":
            // Experience uses multiple API keys: ["min_exp", "max_exp", "isFresher"]
            fieldState.value = {
              min: jobDataValue.minExp || -1,
              max: jobDataValue.maxExp || -1,
              isFresher: jobDataValue.is_fresher_allowed
            };
            break;

          case "salary":
            // Salary uses multiple API keys: ["minSal", "maxSal"]
            fieldState.value = {
              min_salary: jobDataValue.minSal || null,
              max_salary: jobDataValue.maxSal || null,
              incentives: jobDataValue.incentives || null
            };
            break;

          case "skills":
            if (Array.isArray(jobDataValue)) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = jobDataValue.map((skill: any) => ({
                id: skill.option_id,
                label:
                  skill.label ||
                  metaOptions?.find(
                    (metaSkill: any) => metaSkill?.id === skill.option_id
                  )?.label ||
                  ""
              }));
            }
            break;

          case "workingDays":
            if (jobDataValue?.option_id !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue.option_id,
                label:
                  jobDataValue.label ||
                  metaOptions?.find(
                    (metaDay: any) => metaDay?.id === jobDataValue.option_id
                  )?.label ||
                  ""
              };
            }
            break;

          case "distance_preference":
            if (jobDataValue?.option_id !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue.option_id,
                label:
                  jobDataValue.label ||
                  metaOptions?.find(
                    (metaOption: any) =>
                      metaOption?.id === jobDataValue.option_id
                  )?.label ||
                  ""
              };
            }
            break;

          case "interview_type":
            if (jobDataValue?.option_id !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue?.option_id,
                label:
                  jobDataValue?.label ||
                  metaOptions?.find(
                    (metaOption: any) =>
                      metaOption?.id === jobDataValue?.option_id
                  )?.label ||
                  ""
              };
            }
            break;

          case "is_address_same_as_company":
            if (jobDataValue !== undefined) {
              fieldState.value = {
                id: jobDataValue ? "same" : "different",
                label: jobDataValue ? "Yes, it is" : "No, it's different"
              };
            }
            break;

          case "jobBenefits":
            if (Array.isArray(jobDataValue)) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = jobDataValue.map((perk: any) => ({
                id: perk.option_id,
                label:
                  perk.label ||
                  metaOptions?.find(
                    (metaPerk: any) => metaPerk?.id === perk.option_id
                  )?.label ||
                  ""
              }));
            }
            break;

          case "shiftType":
            if (jobDataValue?.option_id !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue.option_id,
                label:
                  jobDataValue.label ||
                  metaOptions?.find(
                    (metaShift: any) => metaShift?.id === jobDataValue.option_id
                  )?.label ||
                  ""
              };
            }
            break;

          case "communication_preference":
            // Communication preference uses multiple API keys: ["call_allowed", "job_contact"]
            const profilePhone = userProfile?.data?.phone || "";
            fieldState.value = {
              checked: jobDataValue.call_allowed || false,
              profilePhone: jobDataValue.job_contact || profilePhone,
              currentPhone: jobDataValue.job_contact || profilePhone,
              isPhoneVerified: true,
              phoneChanged: false,
              isEditing: false,
              editingPhoneValue: ""
            };
            break;

          case "work_location":
            if (jobDataValue !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue,
                label:
                  metaOptions?.find(
                    (metaLocation: any) => metaLocation?.id === jobDataValue
                  )?.label || ""
              };
            }
            break;

          case "depositRequired":
            fieldState.value = {
              id: jobDataValue ? "yes" : "no",
              label: jobDataValue ? "Yes" : "No"
            };
            break;

          case "industry":
            if (Array.isArray(jobDataValue)) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = jobDataValue.map((industry: any) => ({
                id: industry.option_id,
                label:
                  industry.label ||
                  metaOptions?.find(
                    (metaIndustry: any) =>
                      metaIndustry?.id === industry.option_id
                  )?.label ||
                  ""
              }));
            }
            break;

          case "asset":
            if (Array.isArray(jobDataValue)) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = jobDataValue.map((asset: any) => ({
                id: asset.option_id,
                label:
                  asset.label ||
                  metaOptions?.find(
                    (metaAsset: any) => metaAsset?.id === asset.option_id
                  )?.label ||
                  ""
              }));
            }
            break;

          case "famous_location":
            // Famous location uses multiple API keys: ["famous_location_category_id", "famous_location_id"]
            fieldState.value = {
              famous_category_id:
                jobDataValue.famous_location_category_id || null,
              famous_category_label: null, // Would need to look this up from metadata
              famous_location_id: jobDataValue.famous_location_id || null,
              famous_location_label: null // Would need to look this up from metadata
            };
            break;

          case "jobTimings":
            // Job timings uses multiple API keys: ["job_end_time", "job_start_time"]
            fieldState.value = {
              startTime: jobDataValue.job_start_time || "",
              endTime: jobDataValue.job_end_time || ""
            };
            break;

          case "englishFluency":
            if (jobDataValue?.option_id !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue.option_id,
                label:
                  jobDataValue.label ||
                  metaOptions?.find(
                    (metaOption: any) =>
                      metaOption?.id === jobDataValue.option_id
                  )?.label ||
                  ""
              };
            }
            break;

          case "regionalLanguages":
            if (Array.isArray(jobDataValue)) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = jobDataValue.map((lang: any) => ({
                id: lang.option_id,
                label:
                  lang.label ||
                  metaOptions?.find(
                    (metaLang: any) => metaLang?.id === lang.option_id
                  )?.label ||
                  ""
              }));
            }
            break;

          case "depositReason":
          case "depositTime":
            if (jobDataValue?.option_id !== undefined) {
              const metaOptions = getMetaFieldOptions(metaApiKey || "");
              fieldState.value = {
                id: jobDataValue.option_id,
                label:
                  jobDataValue.label ||
                  metaOptions?.find(
                    (metaOption: any) =>
                      metaOption?.id === jobDataValue.option_id
                  )?.label ||
                  ""
              };
            }
            break;

          default:
            // For fields with single API key mapping, use the mapped API key directly
            if (typeof draftApiKeys === "string") {
              fieldState.value = jobDataValue;
            }
            break;
        }
      }
    );

    return updatedFieldStates;
  },

  updateFieldStateWithMeta: (
    fieldStates: FieldState[],
    state: "local" | "final" = "local"
  ) => {
    const { localState, finalState } = get();
    const currentState = state === "local" ? localState : finalState;

    // Create a map of existing fields for efficient lookup
    const existingFieldsMap = new Map(
      currentState.map(field => [field.id, field])
    );

    // Process incoming field states
    const updatedState = [...currentState];

    fieldStates.forEach(newFieldState => {
      const existingField = existingFieldsMap.get(newFieldState.id);

      if (existingField) {
        // Field exists - update all properties except 'value' (preserve existing value)
        const existingIndex = updatedState.findIndex(
          field => field.id === newFieldState.id
        );
        if (existingIndex !== -1) {
          updatedState[existingIndex] = {
            ...newFieldState, // Apply all new properties
            value: existingField.value, // Preserve existing value
            error: existingField.error
          };
        }
      } else {
        // Field doesn't exist - add the new field state completely
        updatedState.push(newFieldState);
      }
    });

    if (state === "local") {
      set({ localState: updatedState });
    } else {
      set({ finalState: updatedState });
    }
  },

  updateFieldStateFromJobData: (
    fieldStates: FieldState[],
    state: "local" | "final" = "local"
  ) => {
    const { localState, finalState } = get();
    const currentState = state === "local" ? localState : finalState;

    // Create a map of existing fields for efficient lookup
    const existingFieldsMap = new Map(
      currentState.map(field => [field.id, field])
    );

    // Process incoming field states from job data
    const updatedState = [...currentState];

    fieldStates.forEach(newFieldState => {
      const existingField = existingFieldsMap.get(newFieldState.id);

      if (existingField) {
        // Field exists - update only the 'value' property, preserve all other existing properties
        const existingIndex = updatedState.findIndex(
          field => field.id === newFieldState.id
        );
        if (existingIndex !== -1) {
          updatedState[existingIndex] = {
            ...existingField, // Preserve all existing properties
            value: newFieldState.value // Update only the value from job data
          };
        }
      } else {
        // Field doesn't exist - add the new field state completely
        updatedState.push(newFieldState);
      }
    });

    if (state === "local") {
      set({ localState: updatedState });

      // Also update the temporary store with the updated data
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.hydrateFromData(updatedState);
    } else {
      set({ finalState: updatedState });

      // Also update the temporary store with the updated data
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.hydrateFromData(updatedState);
    }
  },

  hydrateFromTemporaryStore: () => {
    const tempStore = useTemporaryJobPostingStore.getState();
    const {
      localState: tempLocalState,
      finalState: tempFinalState,
      draftId,
      jobId,
      isDraft
    } = tempStore;
    if (isDraft && jobId) {
      // Payment Pending case.
      get().resetAllFieldErrors();
      setTimeout(() => {
        Router.replace({
          pathname: Routes.JOB_POSTING_FORM,
          query: { job_id: jobId, is_draft: isDraft }
        });
      }, 0);
      return;
    }
    const { localState: currentLocalState, finalState: currentFinalState } =
      get();

    // Create a union of local states with temporary store taking precedence
    const mergedLocalState = [...currentLocalState];

    // Process temporary store fields
    tempLocalState.forEach(tempField => {
      const existingFieldIndex = mergedLocalState.findIndex(
        field => field.id === tempField.id
      );

      if (existingFieldIndex !== -1) {
        // Field exists in current state - replace with temporary store version
        mergedLocalState[existingFieldIndex] = { ...tempField };
      } else {
        // Field doesn't exist in current state - add it from temporary store
        mergedLocalState.push({ ...tempField });
      }
    });

    // Create a union of final states with temporary store taking precedence
    const mergedFinalState = [...currentFinalState];

    // Process temporary store final state fields
    tempFinalState.forEach(tempField => {
      const existingFieldIndex = mergedFinalState.findIndex(
        field => field.id === tempField.id
      );

      if (existingFieldIndex !== -1) {
        // Field exists in current final state - replace with temporary store version
        mergedFinalState[existingFieldIndex] = { ...tempField };
      } else {
        // Field doesn't exist in current final state - add it from temporary store
        mergedFinalState.push({ ...tempField });
      }
    });

    set({
      localState: mergedLocalState,
      finalState: mergedFinalState,
      draftId,
      jobId
    });
  },

  hydrateFromRemoteDraft: async (draftItem: DraftItem) => {
    try {
      // Use the draft job store to fetch draft data
      const draftJobStore = useDraftJobStore.getState();
      const jobData = await draftJobStore.fetchDraftData(draftItem.draft_id);

      if (jobData) {
        const localStateWithValues = get().hydrateFieldValuesFromJobData(
          jobData,
          get().localState
        );

        // Use the new updateFieldStateFromJobData function to preserve existing field metadata
        get().updateFieldStateFromJobData(localStateWithValues, "local");
        get().setDraftId(draftItem.draft_id?.toString());
      } else {
        console.warn("No draft data received from store");
      }
    } catch (error) {
      console.error("Error loading remote draft:", error);
      // Fallback: continue with current state
    }
  },
  setDraftId: (draftId: string) => {
    set({ draftId });
    const tempStore = useTemporaryJobPostingStore.getState();
    tempStore.setDraftId(draftId);
  },
  setJobId: (jobId: string) => {
    set({ jobId });
    const tempStore = useTemporaryJobPostingStore.getState();
    tempStore.setJobId(jobId);
  },
  setJobPlan: (jobPlan: string) => {
    set({ jobPlan });
    const tempStore = useTemporaryJobPostingStore.getState();
    tempStore.setJobPlan(jobPlan);
  },
  setIsDraft: (isDraft: boolean) => {
    set({ isDraft });
    const tempStore = useTemporaryJobPostingStore.getState();
    tempStore.setIsDraft(isDraft);
  },
  clearStore: () => {
    set({
      draftId: undefined,
      jobId: undefined,
      localState: [],
      finalState: [],
      isCategorySetManually: false
    });

    // Also clear the temporary store
    const tempStore = useTemporaryJobPostingStore.getState();
    tempStore.clearStore();
  },
  clearLocalStore: () => {
    set({ localState: [], finalState: [] });
  },

  setCategorySetManually: (isManual: boolean) => {
    set({ isCategorySetManually: isManual });
  },

  updateFieldsOnCategoryChange: () => {
    const { localState, fieldConfigMapping } = get();
    const updatedState = JSON.parse(JSON.stringify(localState));
    const stepperStore = useJobPostingStepperStore.getState();
    const updatedSteps = JSON.parse(JSON.stringify(stepperStore.steps));
    let hasUpdates = false;

    // Loop through fieldConfigMapping and handle affected fields
    Object.entries(fieldConfigMapping).forEach(([key, value]) => {
      const fieldIndex = updatedState.findIndex(field => field.id === key);
      if (fieldIndex === -1) return; // Field not found, skip

      const field = updatedState[fieldIndex];
      if (!field?.visible) return; // Field not visible, skip
      switch (key) {
        case "gender":
        case "qualification":
          // Update fields that have a defaultOption
          const defaultOption = field.defaultOption;
          if (defaultOption) {
            updatedState[fieldIndex] = {
              ...field,
              value: {
                id: defaultOption,
                label: defaultOption
              }
            };
            hasUpdates = true;
          }
          break;

        case "skills":
        case "industry":
          // Reset array fields to empty array if they have values
          if (field?.value?.length) {
            const stepToUpdate = updatedSteps.find(
              step => step.id === field.sectionId
            );
            if (stepToUpdate) {
              stepToUpdate.state = "INCOMPLETE";
            }
            updatedState[fieldIndex] = {
              ...field,
              value: []
            };
            hasUpdates = true;
          }
          break;

        case "industryExp":
          // Reset boolean field to false if it has a truthy value
          if (field?.value) {
            const stepToUpdate = updatedSteps.find(
              step => step.id === field.sectionId
            );
            if (stepToUpdate) {
              stepToUpdate.state = "INCOMPLETE";
            }
            updatedState[fieldIndex] = {
              ...field,
              value: false
            };
            hasUpdates = true;
          }
          break;

        case "job_description":
          // Reset string field to empty string if it has a value
          if (field?.value) {
            const stepToUpdate = updatedSteps.find(
              step => step.id === field.sectionId
            );
            if (stepToUpdate) {
              stepToUpdate.state = "INCOMPLETE";
            }
            updatedState[fieldIndex] = {
              ...field,
              value: ""
            };
            hasUpdates = true;
          }
          break;

        default:
          // For all other fields, do nothing
          break;
      }
    });

    // Only update state if there were actual changes
    if (hasUpdates) {
      set({ localState: updatedState });
      useJobPostingStepperStore.getState().updateAllStepsState(updatedSteps);
      // Also update the temporary store
      const tempStore = useTemporaryJobPostingStore.getState();
      tempStore.hydrateFromData(updatedState);
    }
  },
  shouldAlertUserOnCategoryChange: ({
    id,
    value
  }: {
    id: string;
    value: any;
  }) => {
    const { localState, isCategorySetManually, fieldConfigMapping } = get();
    const categoryField = localState.find(field => field.id === "category_id");
    let shouldAlert = false;
    // avoid alerting if the category is changed to the same value
    if (id === "category_id" && value?.id === categoryField?.value?.id) {
      return false;
    }
    if (id === "job_title") {
      const jobTitles = queryClient.getQueryData(
        jobTitlesKeys.list()
      ) as ApiJobTitlesOptions;
      const mappedJobTitles = mapJobTitlesOptions(jobTitles);
      const selectedJobTitle = mappedJobTitles?.options?.find(
        title => title?.title === value
      );
      if (selectedJobTitle?.categories?.length === 1) {
        const singleCategory = selectedJobTitle.categories[0];
        if (singleCategory.categoryId === categoryField?.value?.id) {
          return false;
        }
      }
    }
    if (!!categoryField?.value?.id && isCategorySetManually) {
      Object.entries(fieldConfigMapping).forEach(([key, value]) => {
        const field = localState.find(field => field.id === key);
        if (!field?.visible) return;
        switch (key) {
          case "skills":
          case "industry":
            if (field?.value?.length) {
              shouldAlert = true;
            }
            break;
          case "industryExp":
          case "job_description":
            if (field?.value) {
              shouldAlert = true;
            }
            break;
          default:
            break;
        }
      });
    }
    return shouldAlert;
  },

  resetAllFieldErrors: (state: "local" | "final" = "local") => {
    const { localState, finalState } = get();
    const targetState = state === "local" ? localState : finalState;

    // Iterate through all fields and reset their error state
    const updatedState = targetState.map(field => {
      if (field.error) {
        return {
          ...field,
          error: {
            isError: false,
            errorMsg: undefined,
            showError: false,
            customError: undefined
          }
        };
      }
      return field;
    });

    // Update the appropriate state
    if (state === "local") {
      set({ localState: updatedState });
    } else {
      set({ finalState: updatedState });
    }

    // Also update the temporary store
    const tempStore = useTemporaryJobPostingStore.getState();
    tempStore.hydrateFromData(updatedState);
  }
}));
